// ----------      START   IMPORT  BLOCK        ----------
import { useState, useCallback, useMemo } from 'react';
import { useUser } from '../Context/UserContext';
import type { AddressRead, AddressUpdate } from '../../Types/schema-helpers';
// ----------      END   IMPORT  BLOCK        ----------

export const useUserMeAddresses = () => {
  const { user, updateUserProfile, updateProfileField, isLoading } = useUser();
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [dataError, setDataError] = useState<string | null>(null);

  // Directly memoize addresses from UserContext
  const userAddresses = useMemo(
    () => (user?.addresses || []) as AddressRead[],
    [user?.addresses]
  );

  /**
   * Updates active state for a specific address and persists via UserContext
   */
  const updateMySelectedAddress = useCallback(
    async (addressId: string) => {
      if (!userAddresses.length) return null;

      setIsDataLoading(true);
      setDataError(null);

      try {
        const updatedList: AddressRead[] = userAddresses.map((addr) => ({
          ...addr,
          active: String(addr.id) === addressId,
        }));

        const sanitizedPayload: AddressUpdate[] = updatedList.map((addr) => ({
          ...(addr.id && String(addr.id).trim().length > 0 ? { id: addr.id } : {}),
          street: addr.street || "",
          number: String(addr.number || ""),
          interior_number: addr.interior_number ? Number(addr.interior_number) : null,
          zip_code: String(addr.zip_code || ""),
          state: addr.state ? (String(addr.state) as any) : "",
          google_maps_url: addr.google_maps_url || null,
          deleted: Boolean(addr.deleted),
        }));

        const updatedUser = await updateUserProfile({
          addresses: sanitizedPayload as any,
        });

        return (updatedUser.addresses || []) as AddressRead[];
      } catch (error: any) {
        console.error("Error updating active address:", error);
        
        let message = "Failed to update active address.";
        if (typeof error?.response?.data?.detail === "string") {
          message = error.response.data.detail;
        } else if (error?.message) {
          message = error.message;
        }

        setDataError(message);
        throw error;
      } finally {
        setIsDataLoading(false);
      }
    },
    [userAddresses, updateUserProfile]
  );

  /**
   * Saves or batch updates user addresses via PATCH /user/profile
   */
  const saveAllAddresses = useCallback(
    async (addressesToSave: AddressRead[]) => {
      setIsDataLoading(true);
      setDataError(null);

      try {
        // Build valid AddressUpdate DTOs (omitting empty string IDs for new addresses)
        const sanitizedAddresses: AddressUpdate[] = addressesToSave.map((addr) => {
          const hasValidId = addr.id && String(addr.id).trim().length > 0;

          return {
            ...(hasValidId ? { id: addr.id } : {}),
            street: addr.street || "",
            number: String(addr.number || ""),
            interior_number: addr.interior_number ? Number(addr.interior_number) : null,
            zip_code: String(addr.zip_code || ""),
            state: addr.state ? (String(addr.state) as any) : "",
            google_maps_url: addr.google_maps_url || null,
            deleted: Boolean(addr.deleted),
          };
        });

        // Delegate partial update execution to UserContext
        const updatedUser = await updateUserProfile({
          addresses: sanitizedAddresses as any,
        });

        return (updatedUser.addresses || []) as AddressRead[];
      } catch (error: any) {
        console.error("Error saving addresses:", error);

        let message = "Failed to save addresses.";
        if (typeof error?.response?.data?.detail === "string") {
          message = error.response.data.detail;
        } else if (Array.isArray(error?.response?.data?.detail)) {
          message = error.response.data.detail
            .map((e: any) => `${e.loc?.slice(-1)[0] || "field"}: ${e.msg}`)
            .join(", ");
        } else if (error?.message) {
          message = error.message;
        }

        setDataError(message);
        throw error;
      } finally {
        setIsDataLoading(false);
      }
    },
    [updateUserProfile]
  );

  return {
    userAddresses,
    isDataLoading: isDataLoading || isLoading,
    dataError,
    updateMySelectedAddress,
    saveAllAddresses,
  };
};
